import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Icon, Checkbox, Button, Tooltip, Badge, message, Popconfirm } from 'antd';

import * as permissionsActions from '~/containers/Permissions/actions';
import { permissionsTree } from '~/components/Permissions/permissions-tree';

// Visão matriz: permissões nas linhas, grupos nas colunas. Permite comparar
// e editar todos os grupos de uma vez, sem abrir o modal grupo a grupo.

var GROUP_COL_WIDTH = 118;
var SOCIO_GROUP_ID = 1;

function filterByPlan(items, isPremiumPlan, allowBetaFeatures) {
    return items.filter(function(item) {
        if (!isPremiumPlan && item.plans && item.plans.indexOf('starter') === -1) {
            return false;
        }
        if (!allowBetaFeatures && item.isBeta) {
            return false;
        }
        return true;
    }).map(function(item) {
        if (item.children) {
            return Object.assign({}, item, {
                children: filterByPlan(item.children, isPremiumPlan, allowBetaFeatures),
            });
        }
        return item;
    });
}

function isRealPermissionKey(key) {
    return key.indexOf('_PARENT') === -1 && key.indexOf('_SECTION') === -1;
}

function getAllKeys(items) {
    var keys = [];
    items.forEach(function(item) {
        if (isRealPermissionKey(item.key)) {
            keys.push(item.key);
        }
        if (item.children) {
            keys = keys.concat(getAllKeys(item.children));
        }
    });
    return keys;
}

// Achata a árvore em linhas de tabela: cabeçalhos (nós com filhos) e
// permissões (folhas), preservando o nível para indentação.
function flattenTree(items, level) {
    var rows = [];
    items.forEach(function(item) {
        var isLeaf = !item.children;
        if (isLeaf && isRealPermissionKey(item.key)) {
            rows.push({ type: 'leaf', item: item, level: level });
            return;
        }
        if (item.children && item.children.length > 0) {
            rows.push({ type: 'header', item: item, level: level, keys: getAllKeys(item.children) });
            rows = rows.concat(flattenTree(item.children, level + 1));
        }
    });
    return rows;
}

function sameKeySet(a, b) {
    if (a.length !== b.length) return false;
    var map = {};
    for (var i = 0; i < a.length; i++) map[a[i]] = true;
    for (var j = 0; j < b.length; j++) {
        if (!map[b[j]]) return false;
    }
    return true;
}

var PermissionsMatrixView = function(props) {
    var groups = props.groups || [];
    var search = props.search || '';

    var dispatch = useDispatch();
    var user = useSelector(function(state) { return state.user; });
    var tenant = (user && user.tenant) || {};
    var isPremiumPlan = tenant.plan === 'premium';
    var allowBetaFeatures = tenant.is_beta_tester;

    // Edições pendentes: { groupId: [chaves] }. Célula lê daqui quando o grupo
    // foi mexido; senão, cai no group.permissions vindo do backend.
    var [edits, setEdits] = useState({});
    var [saving, setSaving] = useState(false);
    var [restoring, setRestoring] = useState(false);

    var tree = filterByPlan(permissionsTree, isPremiumPlan, allowBetaFeatures);
    var allPermissionKeys = getAllKeys(tree);
    var rows = flattenTree(tree, 0);

    // Filtro de busca: mantém as folhas cujo título bate e os cabeçalhos que
    // tenham ao menos uma folha visível.
    if (search) {
        var q = search.toLowerCase();
        var visibleLeafKeys = {};
        rows.forEach(function(row) {
            if (row.type === 'leaf' && row.item.title.toLowerCase().indexOf(q) > -1) {
                visibleLeafKeys[row.item.key] = true;
            }
        });
        rows = rows.filter(function(row) {
            if (row.type === 'leaf') return !!visibleLeafKeys[row.item.key];
            return row.keys.some(function(k) { return visibleLeafKeys[k]; });
        });
    }

    function getGroupPerms(group) {
        if (group.id === SOCIO_GROUP_ID) return allPermissionKeys;
        if (edits[group.id]) return edits[group.id];
        return group.permissions || [];
    }

    function isDirty(group) {
        if (!edits[group.id]) return false;
        return !sameKeySet(edits[group.id], group.permissions || []);
    }

    var dirtyGroups = groups.filter(isDirty);

    function setGroupPerms(group, keys) {
        setEdits(function(prev) {
            var next = Object.assign({}, prev);
            next[group.id] = keys;
            return next;
        });
    }

    function toggleCell(group, key) {
        if (group.id === SOCIO_GROUP_ID || saving) return;
        var current = getGroupPerms(group).slice();
        var index = current.indexOf(key);
        if (index > -1) current.splice(index, 1);
        else current.push(key);
        setGroupPerms(group, current);
    }

    function toggleModule(group, keys, checked) {
        if (group.id === SOCIO_GROUP_ID || saving) return;
        var current = getGroupPerms(group).slice();
        if (checked) {
            keys.forEach(function(key) {
                if (current.indexOf(key) === -1) current.push(key);
            });
        } else {
            current = current.filter(function(key) { return keys.indexOf(key) === -1; });
        }
        setGroupPerms(group, current);
    }

    function discardChanges() {
        setEdits({});
    }

    // Carrega os padrões do sistema (seeder) e aplica como alterações
    // PENDENTES nos grupos padrão — nada é salvo até o usuário revisar.
    function restoreDefaults() {
        setRestoring(true);
        dispatch(permissionsActions.loadDefaultPermissions())
            .then(function(res) {
                var defaults = res && res.payload && res.payload.data && res.payload.data.data;
                if (!defaults) throw new Error('sem-dados');
                var restored = [];
                var nextEdits = Object.assign({}, edits);
                groups.forEach(function(group) {
                    if (group.id === SOCIO_GROUP_ID) return;
                    if (!defaults[group.name]) return;
                    nextEdits[group.id] = defaults[group.name].slice();
                    restored.push(group.name);
                });
                if (restored.length === 0) {
                    message.info('Nenhum grupo com nome padrão do sistema para restaurar.');
                    return;
                }
                setEdits(nextEdits);
                message.info('Padrões carregados em ' + restored.length + ' grupo(s). Revise e clique em "Salvar alterações".', 5);
            })
            .catch(function() {
                message.error('Erro ao carregar os padrões do sistema.');
            })
            .finally(function() {
                setRestoring(false);
            });
    }

    function saveChanges() {
        if (dirtyGroups.length === 0) return;
        setSaving(true);
        // Sequencial de propósito: o reducer aplica cada UPDATE_SUCCESS na
        // lista, e requisições em paralelo poderiam disputar o backend.
        var chain = Promise.resolve();
        dirtyGroups.forEach(function(group) {
            chain = chain.then(function() {
                return dispatch(permissionsActions.update(group.id, {
                    name: group.name,
                    permissions: edits[group.id].filter(isRealPermissionKey),
                }));
            });
        });
        chain
            .then(function() {
                message.success(
                    dirtyGroups.length === 1
                        ? 'Permissões do grupo atualizadas.'
                        : 'Permissões de ' + dirtyGroups.length + ' grupos atualizadas.'
                );
                setEdits({});
            })
            .catch(function() {
                message.error('Erro ao salvar as permissões. Tente novamente.');
            })
            .finally(function() {
                setSaving(false);
            });
    }

    var stickyFirstCol = {
        position: 'sticky',
        left: 0,
        background: '#fff',
        zIndex: 2,
        borderRight: '1px solid #f0f0f0',
    };

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, gap: 12 }}>
                <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                    Marque e desmarque permissões por grupo — as alterações só valem depois de salvar.
                </div>
                <Popconfirm
                    title={'Repor as permissões padrão do sistema nos grupos padrão (Fiscal, Contábil, Legalização, Administrativo, Pessoal e Financeiro)? Nada é salvo até você revisar e clicar em "Salvar alterações".'}
                    okText="Repor padrões"
                    cancelText="Cancelar"
                    placement="bottomRight"
                    onConfirm={restoreDefaults}
                >
                    <Button size="small" icon="undo" loading={restoring} disabled={saving}>
                        Restaurar padrão do sistema
                    </Button>
                </Popconfirm>
            </div>
            <div style={{
                border: '1px solid #f0f0f0', borderRadius: 8, overflow: 'auto',
                height: 'calc(100vh - 275px)', minHeight: 320, background: '#fff',
            }}>
                {/* Hover da linha: precisa de !important para vencer os
                    backgrounds inline das células (inclusive a coluna fixa) */}
                <style>{'.perm-matrix tbody tr:hover td { background: #e6f4ff !important; }'}</style>
                <table className="perm-matrix" style={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%', minWidth: 380 + groups.length * GROUP_COL_WIDTH }}>
                    <thead>
                        <tr>
                            <th style={Object.assign({}, stickyFirstCol, {
                                top: 0, zIndex: 3, minWidth: 320,
                                padding: '10px 14px', textAlign: 'left',
                                fontSize: 12, fontWeight: 600, color: '#595959',
                                borderBottom: '1px solid #f0f0f0', background: '#fafafa',
                            })}>
                                Permissão
                            </th>
                            {groups.map(function(group) {
                                var isSocio = group.id === SOCIO_GROUP_ID;
                                var usersCount = (group.users || []).length;
                                var dirty = isDirty(group);
                                return (
                                    <th key={group.id} style={{
                                        position: 'sticky', top: 0, zIndex: 2,
                                        width: GROUP_COL_WIDTH, minWidth: GROUP_COL_WIDTH,
                                        padding: '10px 8px', textAlign: 'center',
                                        borderBottom: '1px solid #f0f0f0',
                                        background: isSocio ? '#fcfff6' : '#fafafa',
                                        verticalAlign: 'top',
                                    }}>
                                        <Tooltip title={group.name + ' · ' + usersCount + ' usuário(s)' + (isSocio ? ' · acesso total, não editável' : '')}>
                                            <div style={{
                                                fontSize: 12, fontWeight: 600, color: '#262626',
                                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                            }}>
                                                {isSocio && <Icon type="lock" style={{ color: '#52c41a', marginRight: 4, fontSize: 10 }} />}
                                                {group.name}
                                                {dirty && <Badge dot style={{ marginLeft: 4 }} />}
                                            </div>
                                        </Tooltip>
                                        <div style={{ fontSize: 10, color: '#8c8c8c', fontWeight: 400 }}>
                                            {isSocio ? 'acesso total' : usersCount + ' usuário(s)'}
                                        </div>
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(function(row, rowIndex) {
                            // A mesma permissão pode aparecer em mais de um módulo
                            // (ex: plugins.view) — o índice garante keys únicas.
                            if (row.type === 'header') {
                                return (
                                    <tr key={'h-' + row.item.key + '-' + rowIndex}>
                                        <td style={Object.assign({}, stickyFirstCol, {
                                            padding: '8px 14px', paddingLeft: 14 + row.level * 18,
                                            fontSize: 12, fontWeight: 600, color: '#262626',
                                            background: row.level === 0 ? '#f5f5f5' : '#fafafa',
                                            borderBottom: '1px solid #f0f0f0',
                                        })}>
                                            {row.item.icon && <Icon type={row.item.icon} style={{ marginRight: 6, color: '#595959' }} />}
                                            {row.item.title}
                                            {row.item.isBeta && (
                                                <Badge count="BETA" style={{ marginLeft: 6, backgroundColor: '#722ed1', fontSize: 9 }} />
                                            )}
                                        </td>
                                        {groups.map(function(group) {
                                            var isSocio = group.id === SOCIO_GROUP_ID;
                                            var perms = getGroupPerms(group);
                                            var checkedCount = row.keys.filter(function(k) { return perms.indexOf(k) > -1; }).length;
                                            var allChecked = row.keys.length > 0 && checkedCount === row.keys.length;
                                            var someChecked = checkedCount > 0 && !allChecked;
                                            return (
                                                <td key={group.id} style={{
                                                    textAlign: 'center',
                                                    background: isSocio ? '#fcfff6' : row.level === 0 ? '#f5f5f5' : '#fafafa',
                                                    borderBottom: '1px solid #f0f0f0',
                                                }}>
                                                    <Tooltip title={isSocio ? null : (allChecked ? 'Desmarcar tudo de ' + row.item.title : 'Marcar tudo de ' + row.item.title)}>
                                                        <Checkbox
                                                            checked={allChecked}
                                                            indeterminate={someChecked}
                                                            disabled={isSocio || saving}
                                                            onChange={function(e) { toggleModule(group, row.keys, e.target.checked); }}
                                                        />
                                                    </Tooltip>
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            }

                            return (
                                <tr key={row.item.key + '-' + rowIndex}>
                                    <td style={Object.assign({}, stickyFirstCol, {
                                        padding: '7px 14px', paddingLeft: 14 + row.level * 18,
                                        fontSize: 12, color: '#595959',
                                        borderBottom: '1px solid #fafafa', whiteSpace: 'nowrap',
                                    })}>
                                        {row.item.title}
                                        {row.item.info && (
                                            <Tooltip title={row.item.info} placement="top">
                                                <Icon type="info-circle" style={{ fontSize: 11, color: '#d9d9d9', marginLeft: 5, cursor: 'help' }} />
                                            </Tooltip>
                                        )}
                                    </td>
                                    {groups.map(function(group) {
                                        var isSocio = group.id === SOCIO_GROUP_ID;
                                        var checked = getGroupPerms(group).indexOf(row.item.key) > -1;
                                        return (
                                            <td key={group.id} style={{
                                                textAlign: 'center',
                                                background: isSocio ? '#fcfff6' : '#fff',
                                                borderBottom: '1px solid #fafafa',
                                            }}>
                                                <Checkbox
                                                    checked={checked}
                                                    disabled={isSocio || saving}
                                                    onChange={function() { toggleCell(group, row.item.key); }}
                                                />
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                        {rows.length === 0 && (
                            <tr>
                                <td colSpan={groups.length + 1} style={{ padding: 24, textAlign: 'center', color: '#8c8c8c', fontSize: 13 }}>
                                    Nenhuma permissão encontrada para "{search}".
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Barra de salvamento — só aparece quando há alterações */}
            {dirtyGroups.length > 0 && (
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '10px 14px', marginTop: 12,
                    background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 8,
                }}>
                    <Icon type="exclamation-circle" style={{ color: '#faad14' }} />
                    <span style={{ flex: 1, fontSize: 13, color: '#595959' }}>
                        {dirtyGroups.length === 1
                            ? <span>Alterações não salvas no grupo <strong>{dirtyGroups[0].name}</strong>.</span>
                            : <span><strong>{dirtyGroups.length} grupos</strong> com alterações não salvas: {dirtyGroups.map(function(g) { return g.name; }).join(', ')}.</span>}
                    </span>
                    <Button onClick={discardChanges} disabled={saving}>Descartar</Button>
                    <Button type="primary" icon="save" loading={saving} onClick={saveChanges}>
                        Salvar alterações
                    </Button>
                </div>
            )}
        </div>
    );
};

export default PermissionsMatrixView;
